/**
 * Purpose:
 *   Shared client session snapshot with single-flight /api/auth/session fetches.
 *
 * Notes:
 *   All useSessionAccount subscribers read the same store (P-02 dedupe).
 */

export interface SessionAccountSnapshot {
  account_id: string | null;
  user_id: string | null;
  user_name: string | null;
  permissions: string[];
  loading: boolean;
  error: string | null;
}

type SessionListener = () => void;

const initialSnapshot: SessionAccountSnapshot = {
  account_id: null,
  user_id: null,
  user_name: null,
  permissions: [],
  loading: true,
  error: null,
};

let snapshot: SessionAccountSnapshot = initialSnapshot;
let inFlight: Promise<void> | null = null;
let activeReload: Promise<void> | null = null;
let hasLoaded = false;
let latestGeneration = 0;
let lastLoadedAt = 0;
const listeners = new Set<SessionListener>();
const DEFAULT_SESSION_REVALIDATE_MS = 30_000;

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

async function fetchSession(requestGeneration: number): Promise<void> {
  snapshot = { ...snapshot, loading: true, error: null };
  emit();

  try {
    const res = await fetch('/api/auth/session', {
      credentials: 'include',
      cache: 'no-store',
    });
    const data = (await res.json()) as {
      account_id?: string;
      user_id?: string;
      user_name?: string;
      permissions?: unknown;
    };

    if (requestGeneration !== latestGeneration) {
      return;
    }

    snapshot = {
      account_id: data.account_id ?? null,
      user_id: data.user_id ?? null,
      user_name: data.user_name ?? null,
      permissions: Array.isArray(data.permissions)
        ? data.permissions.filter((permission): permission is string => typeof permission === 'string')
        : [],
      loading: false,
      error: null,
    };
    hasLoaded = true;
    lastLoadedAt = Date.now();
  } catch (error) {
    if (requestGeneration !== latestGeneration) {
      return;
    }

    snapshot = {
      account_id: null,
      user_id: null,
      user_name: null,
      permissions: [],
      loading: false,
      error: error instanceof Error ? error.message : 'Failed to load session',
    };
  }

  emit();
}

function startFetch(): Promise<void> {
  const requestGeneration = ++latestGeneration;
  const promise = fetchSession(requestGeneration);
  const tracked = promise.finally(() => {
    if (inFlight === tracked) {
      inFlight = null;
    }
  });
  inFlight = tracked;
  return tracked;
}

function ensureLoaded(): void {
  if (hasLoaded || inFlight || activeReload) {
    return;
  }

  void startFetch();
}

export function subscribeSessionAccount(listener: SessionListener): () => void {
  listeners.add(listener);
  ensureLoaded();

  return () => {
    listeners.delete(listener);
  };
}

export function getSessionAccountSnapshot(): SessionAccountSnapshot {
  return snapshot;
}

export function getServerSessionAccountSnapshot(): SessionAccountSnapshot {
  return initialSnapshot;
}

export function reloadSessionAccount(): Promise<void> {
  if (activeReload) {
    return activeReload;
  }

  hasLoaded = false;
  latestGeneration += 1;

  activeReload = (async () => {
    if (inFlight) {
      await inFlight;
    }
    await startFetch();
  })().finally(() => {
    activeReload = null;
  });

  return activeReload;
}

export function ensureFreshSessionAccount(
  maxAgeMs = DEFAULT_SESSION_REVALIDATE_MS
): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  const age = Date.now() - lastLoadedAt;
  const hasFreshSnapshot = hasLoaded && !snapshot.error && age <= maxAgeMs;

  if (hasFreshSnapshot) {
    return Promise.resolve();
  }

  if (activeReload) {
    return activeReload;
  }

  if (inFlight) {
    return inFlight;
  }

  return reloadSessionAccount();
}

export function resetSessionAccountStoreForTests(): void {
  snapshot = initialSnapshot;
  inFlight = null;
  activeReload = null;
  hasLoaded = false;
  latestGeneration = 0;
  lastLoadedAt = 0;
  listeners.clear();
}
